import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FounderRole } from '../../models/membership.type';
import { MembershipService } from '../../services/auth/membership.service';

export interface NewBusinessDialogData {
  /** Names of clinics the user already belongs to (shown in the no-sharing notice). */
  existingBusinessNames: string[];
}

/** Closes with the new businessId, or undefined when cancelled. */
@Component({
  selector: 'app-new-business-dialog',
  templateUrl: './new-business-dialog.component.html',
  styleUrls: ['./new-business-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatSelectModule,
    MatProgressSpinnerModule,
  ],
})
export class NewBusinessDialogComponent {
  readonly dialogRef = inject<MatDialogRef<NewBusinessDialogComponent, string>>(MatDialogRef);
  readonly data = inject<NewBusinessDialogData>(MAT_DIALOG_DATA);
  private fb = inject(FormBuilder);
  private membership = inject(MembershipService);

  readonly hasOtherBusinesses = this.data.existingBusinessNames.length > 0;
  readonly existingNamesLabel = new Intl.ListFormat('es', { type: 'conjunction' }).format(
    this.data.existingBusinessNames
  );
  readonly busy = signal(false);
  readonly error = signal('');

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
    role: ['doctor' as FounderRole, Validators.required],
    acknowledged: [!this.hasOtherBusinesses, Validators.requiredTrue],
  });

  readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });

  async create(): Promise<void> {
    if (this.form.invalid || this.busy()) {
      this.form.markAllAsTouched();
      return;
    }
    this.busy.set(true);
    this.error.set('');
    this.dialogRef.disableClose = true;
    try {
      const businessId = await this.membership.createOwnedBusiness(
        this.form.controls.name.value.trim(),
        this.form.controls.role.value
      );
      this.dialogRef.close(businessId);
    } catch (err) {
      console.error('[NewBusinessDialog] create failed', err);
      this.error.set(
        err instanceof Error && err.message
          ? err.message
          : 'No se pudo registrar el consultorio. Intenta de nuevo.'
      );
      this.busy.set(false);
      this.dialogRef.disableClose = false;
    }
  }
}
